import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Card, Select, Empty, Switch, Button, Row, Col, Tooltip, Modal, Input, message, Popconfirm, Divider } from 'antd';
import {
	BarChartOutlined,
	LineChartOutlined,
	AreaChartOutlined,
	PieChartOutlined,
	DotChartOutlined,
	RadarChartOutlined,
	FunnelPlotOutlined,
	DatabaseOutlined,
	PlusOutlined,
	DeleteOutlined,
	CopyOutlined,
	LeftOutlined,
	RightOutlined,
	FolderOpenOutlined,
	EditOutlined,
	StarOutlined,
	StarFilled,
} from '@ant-design/icons';
import {
	Pie as AntPie,
	Column as AntColumn,
	Bar as AntBar,
	Line as AntLine,
	Area as AntArea,
	Scatter as AntScatter,
	Rose as AntRose,
	Radar as AntRadar,
	Funnel as AntFunnel,
} from '@ant-design/charts';
import moment from 'moment';
import { safeMoment } from '../../../../utils/safeDate.js';
import { useTranslation } from 'react-i18next';
import { saveGraphView, getGraphViews, deleteGraphView, setGraphViewDefault } from '../../../../services/GraphViewService.js';

// Validated categorical palette (fixed slot order — do not re-sort/cycle)
const CATEGORICAL_PALETTE = ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834'];
const BRAND = '#141BCB';

const COUNT_FIELD = '__count__';
const MAX_CATEGORIES = 8; // palette size — beyond this, extra categories fold into "Other"
const CARD_CHART_HEIGHT = 300;
const STORAGE_VERSION = 'ff_graphview_v1';

const CHART_TYPES = [
	{ value: 'column', label: 'Column', icon: <BarChartOutlined /> },
	{ value: 'bar', label: 'Bar', icon: <BarChartOutlined style={{ transform: 'rotate(90deg) scaleX(-1)' }} /> },
	{ value: 'line', label: 'Line', icon: <LineChartOutlined /> },
	{ value: 'area', label: 'Area', icon: <AreaChartOutlined /> },
	{ value: 'pie', label: 'Pie', icon: <PieChartOutlined /> },
	{ value: 'donut', label: 'Donut', icon: <PieChartOutlined rotate={180} /> },
	{ value: 'rose', label: 'Rose', icon: <PieChartOutlined /> },
	{ value: 'radar', label: 'Radar', icon: <RadarChartOutlined /> },
	{ value: 'funnel', label: 'Funnel', icon: <FunnelPlotOutlined /> },
	{ value: 'scatter', label: 'Scatter', icon: <DotChartOutlined /> },
];

// "Sliced" charts colour by a single category dimension (no group-by) and fold the tail into "Other"
const SLICED_TYPES = new Set(['pie', 'donut', 'rose', 'funnel']);

// Aggregations available for a numeric Y column
const NUMERIC_AGGS = [
	{ value: 'sum', label: 'Sum' },
	{ value: 'avg', label: 'Average' },
	{ value: 'min', label: 'Min' },
	{ value: 'max', label: 'Max' },
	{ value: 'count', label: 'Count' },
];
// Aggregations available for a text / date Y column (can't sum text)
const CATEGORICAL_AGGS = [
	{ value: 'count', label: 'Count' },
	{ value: 'countDistinct', label: 'Count Distinct' },
];
const AGG_LABEL = { sum: 'Sum', avg: 'Average', min: 'Min', max: 'Max', count: 'Count', countDistinct: 'Count Distinct' };

// Fields on request records that never make sense as chart axes
const EXCLUDED_KEYS = new Set([
	'key', 'value', 'originalWorkflow', 'workflow', 'paymentSchedule', 'chosen',
	'requestId', '_id', 'id', 'workflowId', 'spaceId', 'tenantId', 'formData', 'children',
]);

const isGuidLike = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

// Turn a camelCase / snake_case key into a readable label
const prettifyKey = (k) => String(k)
	.replace(/[_-]+/g, ' ')
	.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
	.replace(/\s+/g, ' ')
	.trim()
	.replace(/\b\w/g, (c) => c.toUpperCase());

// Coerce any cell value to a readable category label
const toLabel = (v) => {
	if (v === null || v === undefined || v === '') return 'N/A';
	if (typeof v === 'object') return v.name || v.label || v.title || v.email || 'N/A';
	return String(v);
};

const toNumber = (v) => {
	if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
	if (typeof v === 'string' && v.trim() !== '') {
		const n = Number(v);
		if (!Number.isNaN(n)) return n;
	}
	return undefined;
};

// unique id for a chart card (plain app code — Date.now/random are fine here)
let __uidCounter = 0;
const uid = () => `c_${Date.now().toString(36)}_${(__uidCounter++).toString(36)}`;

const defaultConfig = (fields) => {
	const prefer = ['status', 'createdBy', 'requester'];
	const keys = (fields.all || []).map((f) => f.value);
	const xDefault = prefer.find((p) => keys.includes(p)) || keys[0];
	return { chartType: 'column', xField: xDefault, yField: COUNT_FIELD, aggregation: 'sum', seriesField: undefined, stacked: false };
};

const deriveFlags = (config, typeOf) => {
	const isScatter = config.chartType === 'scatter';
	const isTimeChart = config.chartType === 'line' || config.chartType === 'area';
	const isSliced = SLICED_TYPES.has(config.chartType);
	const usesCount = config.yField === COUNT_FIELD;
	const yType = usesCount ? 'count' : typeOf(config.yField);
	const yIsNumeric = yType === 'numeric';
	return { isScatter, isTimeChart, isSliced, usesCount, yType, yIsNumeric };
};

const computeTitles = (config, labelOf, t) => {
	const { isScatter, usesCount } = deriveFlags(config, () => 'categorical');
	const yTitle = usesCount
		? t('Count of Requests')
		: `${t(AGG_LABEL[config.aggregation] || 'Value')} ${t('of')} ${labelOf(config.yField)}`;
	const chartTitle = isScatter
		? `${labelOf(config.yField)} ${t('vs')} ${labelOf(config.xField)}`
		: `${yTitle} ${t('by')} ${labelOf(config.xField)}`;
	return { yTitle, chartTitle };
};

// ---- Map a builder chart to the /graph-view API shape (pure) ----
const chartToApiPayload = (chart, labelOf, t) => {
	const { config } = chart;
	const usesCount = config.yField === COUNT_FIELD;
	return {
		name: (config.title && config.title.trim()) || computeTitles(config, labelOf, t).chartTitle,
		chartType: config.chartType,
		xAxis: config.xField || null,
		yAxis: usesCount ? 'count' : (config.yField || null),
		aggregation: usesCount ? 'count' : (config.aggregation || null),
		groupBy: config.seriesField || null,
	};
};

// ---- Map a saved /graph-view chart back to a builder chart (pure) ----
const apiChartToConfig = (c = {}) => {
	const usesCount = !c.yAxis || c.yAxis === 'count' || c.yAxis === COUNT_FIELD;
	return {
		chartType: c.chartType || 'column',
		xField: c.xAxis || undefined,
		yField: usesCount ? COUNT_FIELD : c.yAxis,
		aggregation: c.aggregation || 'sum',
		seriesField: c.groupBy || undefined,
		stacked: false,
		title: c.name || '',
	};
};

// Normalize a saved graph-view record from the API into a stable shape
const normalizeGraphView = (v = {}) => ({
	id: v.graphViewId || v._id || v.id,
	name: v.name,
	sharedWithUsers: !!v.sharedWithUsers,
	defaultViewers: Array.isArray(v.defaultViewers) ? v.defaultViewers : [],
	charts: Array.isArray(v.charts) ? v.charts : [],
});

// Pull an array of records out of whatever envelope the API returns
const extractGraphViewList = (res) => {
	const r = res?.result ?? res?.data ?? res;
	if (Array.isArray(r)) return r;
	if (Array.isArray(r?.graphViews)) return r.graphViews;
	if (Array.isArray(r?.result)) return r.result;
	return [];
};

// ---- Aggregate rows into a chart dataset (pure) ----
const buildChartData = (rows, config, typeOf, t) => {
	const { isScatter, isTimeChart, isSliced, usesCount, yIsNumeric } = deriveFlags(config, typeOf);
	const groupValue = (rec, key) => {
		let v = rec?.[key];
		if (typeOf(key) === 'date' && v) {
			// Values can arrive as a dayjs (live or prototype-less) as well as a
			// string, and plain moment() reads those as Invalid date.
			const asMoment = safeMoment(v);
			if (asMoment.isValid()) v = asMoment.format('YYYY-MM-DD');
		}
		return toLabel(v);
	};

	if (!rows.length) return [];
	if (isScatter) {
		const out = [];
		rows.forEach((rec) => {
			const x = toNumber(rec?.[config.xField]);
			const y = toNumber(rec?.[config.yField === COUNT_FIELD ? undefined : config.yField]);
			if (x === undefined || y === undefined) return;
			const point = { x, y };
			if (config.seriesField) point.series = groupValue(rec, config.seriesField);
			out.push(point);
		});
		return out;
	}
	if (!config.xField) return [];

	const xIsDate = typeOf(config.xField) === 'date';
	// collect the raw values that feed the aggregation, per group
	const groups = new Map(); // key -> value[]
	rows.forEach((rec) => {
		const x = groupValue(rec, config.xField);
		const s = config.seriesField ? groupValue(rec, config.seriesField) : undefined;
		const key = s !== undefined ? `${x}||${s}` : x;
		if (!groups.has(key)) groups.set(key, []);
		const bucket = groups.get(key);
		if (usesCount) {
			bucket.push(1);
		} else if (yIsNumeric) {
			const n = toNumber(rec?.[config.yField]);
			if (n !== undefined) bucket.push(n);
		} else {
			// text / date Y — keep the (bucketed) label so we can count / count-distinct it
			const raw = rec?.[config.yField];
			if (raw !== null && raw !== undefined && raw !== '') bucket.push(groupValue(rec, config.yField));
		}
	});

	const aggregate = (values) => {
		if (usesCount) return values.length;
		if (!yIsNumeric) {
			return config.aggregation === 'count' ? values.length : new Set(values).size;
		}
		if (!values.length) return 0;
		switch (config.aggregation) {
			case 'avg': return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100;
			case 'min': return Math.min(...values);
			case 'max': return Math.max(...values);
			case 'count': return values.length;
			case 'sum':
			default: return values.reduce((a, b) => a + b, 0);
		}
	};

	let out = [];
	groups.forEach((values, key) => {
		const [x, s] = key.includes('||') ? key.split('||') : [key, undefined];
		out.push({ x, value: aggregate(values), ...(s !== undefined ? { series: s } : {}) });
	});

	if (xIsDate || isTimeChart) {
		out.sort((a, b) => {
			const da = moment(a.x, 'YYYY-MM-DD', true);
			const db = moment(b.x, 'YYYY-MM-DD', true);
			if (da.isValid() && db.isValid()) return da - db;
			return String(a.x).localeCompare(String(b.x));
		});
	} else {
		// show all categories, largest first
		out.sort((a, b) => b.value - a.value);
	}

	// Series cap: palette has 8 slots — keep the top 7 series by total, fold the rest into "Other"
	if (config.seriesField && out.some((d) => d.series !== undefined)) {
		const totals = new Map();
		out.forEach((d) => totals.set(d.series, (totals.get(d.series) || 0) + Math.abs(d.value ?? 0)));
		if (totals.size > MAX_CATEGORIES) {
			const kept = new Set(Array.from(totals.entries()).sort((a, b) => b[1] - a[1]).slice(0, MAX_CATEGORIES - 1).map(([s]) => s));
			const next = [];
			const folded = new Map();
			out.forEach((d) => {
				if (kept.has(d.series)) { next.push(d); return; }
				folded.set(d.x, (folded.get(d.x) || 0) + (d.value ?? 0));
			});
			folded.forEach((value, x) => next.push({ x, value, series: t('Other') }));
			out = next;
		}
	}

	// Pie/Donut/Rose/Funnel: cap slices at the palette size, fold the tail into "Other"
	if (isSliced && out.length > MAX_CATEGORIES) {
		const head = out.slice(0, MAX_CATEGORIES - 1);
		const tail = out.slice(MAX_CATEGORIES - 1);
		head.push({ x: t('Other'), value: tail.reduce((a, b) => a + b.value, 0) });
		out = head;
	}
	return out;
};

// ---- Render a chart element for a config + dataset (pure) ----
const renderChartElement = (config, chartData, { rowsCount, typeOf, labelOf, t, height }) => {
	const { isScatter, isSliced, usesCount, yIsNumeric } = deriveFlags(config, typeOf);
	const { yTitle } = computeTitles(config, labelOf, t);

	if (!rowsCount) return <Empty description={t('No requests to visualize')} style={{ padding: '64px 0' }} />;
	if (!isScatter && !config.xField) return <Empty description={t('Select an X axis column to build the chart')} style={{ padding: '64px 0' }} />;
	if (isScatter && (usesCount || !yIsNumeric)) {
		return <Empty description={t('Scatter needs numeric X and Y axis columns')} style={{ padding: '64px 0' }} />;
	}
	if (!chartData.length) return <Empty description={t('No chartable values for this selection')} style={{ padding: '64px 0' }} />;

	const hasSeries = !isSliced && !!config.seriesField && chartData.some((d) => d.series !== undefined);
	const common = {
		height,
		data: chartData,
		scale: { color: { range: CATEGORICAL_PALETTE } },
		animate: { enter: { type: 'fadeIn' } },
	};
	const axisTitles = {
		axis: {
			x: {
				title: labelOf(config.xField),
				titleSpacing: 12,
				titleFill: '#6b7280',
				labelAutoRotate: true,
				labelAutoEllipsis: true,
				labelAutoHide: false,
				labelFontSize: 11,
				labelFill: '#6b7280',
				line: true,
				lineStroke: '#e5e7eb',
				tick: false,
			},
			y: {
				title: isScatter ? labelOf(config.yField) : yTitle,
				titleSpacing: 12,
				titleFill: '#6b7280',
				labelFontSize: 11,
				labelFill: '#6b7280',
				tick: false,
				gridStroke: '#f0f0f0',
			},
		},
	};
	const bottomLegend = { color: { position: 'bottom', itemLabelFontSize: 12, itemLabelFill: '#374151' } };

	switch (config.chartType) {
		case 'bar':
			return (
				<AntBar
					{...common}
					{...axisTitles}
					xField="x"
					yField="value"
					{...(hasSeries
						? {
							colorField: 'series',
							legend: bottomLegend,
							style: { maxWidth: 22, radiusTopRight: 3, radiusBottomRight: 3 },
							...(config.stacked ? { stack: true } : { group: true }),
						}
						: { style: { fill: CATEGORICAL_PALETTE[0], radiusTopRight: 4, radiusBottomRight: 4, maxWidth: 32 }, legend: false })}
				/>
			);
		case 'line':
			return (
				<AntLine
					{...common}
					{...axisTitles}
					xField="x"
					yField="value"
					{...(hasSeries ? { colorField: 'series', legend: bottomLegend } : { style: { stroke: CATEGORICAL_PALETTE[0] } })}
					style={{ lineWidth: 2 }}
					point={{ sizeField: 3 }}
				/>
			);
		case 'area':
			return (
				<AntArea
					{...common}
					{...axisTitles}
					xField="x"
					yField="value"
					{...(hasSeries ? { colorField: 'series', legend: bottomLegend, stack: true } : {})}
					style={{ fillOpacity: 0.5 }}
					line={{ style: { lineWidth: 2 } }}
				/>
			);
		case 'pie':
		case 'donut':
			return (
				<AntPie
					{...common}
					angleField="value"
					colorField="x"
					radius={0.85}
					{...(config.chartType === 'donut' ? { innerRadius: 0.6 } : {})}
					label={{ text: (d) => `${d.x}: ${d.value}`, position: 'outside', fontSize: 12, fill: '#374151' }}
					legend={bottomLegend}
				/>
			);
		case 'rose':
			return (
				<AntRose
					{...common}
					xField="x"
					yField="value"
					colorField="x"
					radius={0.9}
					label={{ text: (d) => `${d.value}`, fontSize: 11, fill: '#fff' }}
					legend={bottomLegend}
				/>
			);
		case 'funnel':
			return (
				<AntFunnel
					{...common}
					xField="x"
					yField="value"
					colorField="x"
					label={{ text: (d) => `${d.x}: ${d.value}`, fontSize: 12 }}
					legend={bottomLegend}
				/>
			);
		case 'radar':
			return (
				<AntRadar
					{...common}
					xField="x"
					yField="value"
					scale={{ color: { range: CATEGORICAL_PALETTE }, x: { padding: 0.5, align: 0 }, y: { nice: true } }}
					axis={{ x: { labelFontSize: 11, labelFill: '#6b7280' } }}
					area={{ style: { fillOpacity: 0.25 } }}
					style={{ lineWidth: 2 }}
					{...(hasSeries
						? { colorField: 'series', legend: bottomLegend }
						: { style: { lineWidth: 2, stroke: CATEGORICAL_PALETTE[0], fill: CATEGORICAL_PALETTE[0], fillOpacity: 0.25 }, legend: false })}
				/>
			);
		case 'scatter':
			return (
				<AntScatter
					{...common}
					xField="x"
					yField="y"
					sizeField={4}
					shapeField="point"
					axis={{
						x: { title: labelOf(config.xField), titleSpacing: 12, titleFill: '#6b7280', labelFontSize: 11, labelFill: '#6b7280', tick: false },
						y: { title: labelOf(config.yField), titleSpacing: 12, titleFill: '#6b7280', labelFontSize: 11, labelFill: '#6b7280', tick: false, gridStroke: '#f0f0f0' },
					}}
					{...(config.seriesField && chartData.some((d) => d.series !== undefined)
						? { colorField: 'series', legend: bottomLegend }
						: { style: { fill: CATEGORICAL_PALETTE[0] } })}
				/>
			);
		case 'column':
		default:
			return (
				<AntColumn
					{...common}
					{...axisTitles}
					xField="x"
					yField="value"
					{...(hasSeries
						? {
							colorField: 'series',
							legend: bottomLegend,
							style: { maxWidth: 22, radiusTopLeft: 3, radiusTopRight: 3 },
							...(config.stacked ? { stack: true } : { group: true }),
						}
						: { style: { fill: CATEGORICAL_PALETTE[0], radiusTopLeft: 4, radiusTopRight: 4, maxWidth: 40 }, legend: false })}
				/>
			);
	}
};

// Small labelled control for the config panel
const Field = ({ label, hint, children }) => (
	<div>
		<div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.045em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 7 }}>
			{label}
		</div>
		{children}
		{hint ? <div style={{ marginTop: 5, fontSize: 11, lineHeight: 1.4, color: '#aab0ba' }}>{hint}</div> : null}
	</div>
);

// ============ Left config panel (edits the SELECTED chart) ============
const ConfigPanel = ({ config, fields, typeOf, onChange, t, collapsed, onToggleCollapse, onResizeComplete }) => {
	const panelWidth = 320;
	const collapsedWidth = 52;
	const motion = '280ms cubic-bezier(0.22, 1, 0.36, 1)';
	const panelTitle = (
		<div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
			<span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{t('Visualization')}</span>
			<span style={{ marginTop: 3, fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>{t('Chart settings')}</span>
		</div>
	);
	const collapseButton = (
		<Tooltip title={t('Collapse visualization')}>
			<Button
				type="text"
				shape="circle"
				size="small"
				aria-label={t('Collapse visualization')}
				aria-expanded="true"
				aria-controls="visualization-controls"
				onClick={onToggleCollapse}
				icon={<LeftOutlined />}
				style={{ color: '#4b5563' }}
			/>
		</Tooltip>
	);

	const { isScatter, isSliced, usesCount, yIsNumeric } = config
		? deriveFlags(config, typeOf)
		: { isScatter: false, isSliced: false, usesCount: true, yIsNumeric: false };
	const aggOptions = yIsNumeric ? NUMERIC_AGGS : CATEGORICAL_AGGS;
	const xOptions = isScatter ? fields.numeric : fields.all;
	const yOptions = isScatter
		? fields.numeric
		: [{ value: COUNT_FIELD, label: t('Count of Requests') }, ...fields.all];

	const handleChartType = (v) => {
		if (!config) return;
		const patch = { chartType: v };
		if (v === 'scatter') {
			const numKeys = fields.numeric.map((f) => f.value);
			if (!numKeys.includes(config.xField)) patch.xField = numKeys[0];
			if (!numKeys.includes(config.yField)) patch.yField = numKeys[1] || numKeys[0];
		}
		onChange(patch);
	};

	const handleYChange = (v) => {
		if (!config) return;
		const nextType = v === COUNT_FIELD ? 'count' : typeOf(v);
		let agg = config.aggregation;
		if (nextType === 'numeric') {
			if (!NUMERIC_AGGS.some((a) => a.value === agg)) agg = 'sum';
		} else if (!CATEGORICAL_AGGS.some((a) => a.value === agg)) {
			agg = 'countDistinct';
		}
		onChange({ yField: v, aggregation: agg });
	};

	return (
		<aside
			aria-label={t('Visualization')}
			style={{
				position: 'relative',
				width: collapsed ? collapsedWidth : panelWidth,
				flex: `0 0 ${collapsed ? collapsedWidth : panelWidth}px`,
				alignSelf: 'stretch',
				overflow: 'hidden',
				transition: `width ${motion}, flex-basis ${motion}`,
			}}
			onTransitionEnd={(event) => {
				if (event.propertyName === 'width') onResizeComplete?.();
			}}
		>
			<div
				style={{
					width: panelWidth,
					opacity: collapsed ? 0 : 1,
					transform: collapsed ? 'translateX(-10px)' : 'translateX(0)',
					pointerEvents: collapsed ? 'none' : 'auto',
					transition: `opacity ${motion}, transform ${motion}`,
				}}
			>
				<Card
					variant="borderless"
					title={panelTitle}
					extra={collapseButton}
					style={{ width: panelWidth, height: '100%', boxShadow: 'none' }}
					styles={{
						header: { minHeight: 58, padding: '0 8px 0 4px', borderBottom: '1px solid #f0f1f3' },
						body: { padding: '18px 8px 8px 4px' },
					}}
				>
					<div id="visualization-controls">
						{config ? (
							<div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16, borderRadius: 12, background: '#fafbfc', border: '1px solid #eef0f3' }}>
								<Field label={t('Chart Name')}>
									<Input
										className="w-full"
										allowClear
										placeholder={t('Auto (from axes)')}
										value={config.title}
										onChange={(e) => onChange({ title: e.target.value })}
										maxLength={120}
									/>
								</Field>

								<Field label={t('Chart Type')}>
									<Select
										className="w-full"
										value={config.chartType}
										onChange={handleChartType}
										optionLabelProp="label"
										options={CHART_TYPES.map((ct) => ({
											value: ct.value,
											label: (
												<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
													<span style={{ color: BRAND, display: 'inline-flex' }}>{ct.icon}</span>
													{t(ct.label)}
												</span>
											),
										}))}
									/>
								</Field>

								<Field label={isSliced ? t('Category') : t('X Axis')}>
									<Select
										className="w-full"
										showSearch
										optionFilterProp="label"
										placeholder={t('Select column')}
										value={config.xField}
										options={xOptions}
										onChange={(v) => onChange({ xField: v })}
									/>
								</Field>

								<Field label={isSliced ? t('Values') : t('Y Axis')}>
									<Select
										className="w-full"
										showSearch
										optionFilterProp="label"
										placeholder={t('Select column')}
										value={config.yField}
										options={yOptions}
										onChange={handleYChange}
									/>
								</Field>

								{!isScatter && !usesCount && (
									<Field label={t('Aggregation')}>
										<Select
											className="w-full"
											value={config.aggregation}
											options={aggOptions.map((a) => ({ value: a.value, label: t(a.label) }))}
											onChange={(v) => onChange({ aggregation: v })}
										/>
									</Field>
								)}

								{!isSliced && (
									<Field label={t('Group By (Legend)')}>
										<Select
											className="w-full"
											showSearch
											optionFilterProp="label"
											allowClear
											placeholder={t('None')}
											value={config.seriesField}
											options={fields.all.filter((f) => f.value !== config.xField)}
											onChange={(v) => onChange({ seriesField: v })}
										/>
									</Field>
								)}

								{['column', 'bar'].includes(config.chartType) && config.seriesField && (
									<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, borderTop: '1px solid #eef0f3' }}>
										<span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
											<span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{t('Stacked')}</span>
											<span style={{ fontSize: 11, color: '#aab0ba' }}>{t('Stack series on top of each other')}</span>
										</span>
										<Switch size="small" checked={config.stacked} onChange={(v) => onChange({ stacked: v })} />
									</div>
								)}
							</div>
						) : (
							<div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '32px 8px' }}>
								{t('Select a chart to edit, or add a new one.')}
							</div>
						)}
					</div>
				</Card>
			</div>

			<div
				style={{
					position: 'absolute',
					inset: 0,
					width: collapsedWidth,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: 14,
					paddingTop: 9,
					background: '#fff',
					borderRight: '1px solid #eef0f3',
					opacity: collapsed ? 1 : 0,
					pointerEvents: collapsed ? 'auto' : 'none',
					transition: `opacity ${collapsed ? '180ms 100ms' : '120ms'} ease`,
				}}
			>
				<Tooltip title={t('Expand visualization')} placement="right">
					<Button
						type="text"
						shape="circle"
						aria-label={t('Expand visualization')}
						aria-expanded="false"
						aria-controls="visualization-controls"
						onClick={onToggleCollapse}
						icon={<RightOutlined />}
						style={{ color: BRAND }}
					/>
				</Tooltip>
				<span
					aria-hidden="true"
					style={{
						writingMode: 'vertical-rl',
						transform: 'rotate(180deg)',
						fontSize: 11,
						fontWeight: 600,
						letterSpacing: '0.06em',
						textTransform: 'uppercase',
						color: '#6b7280',
					}}
				>
					{t('Visualization')}
				</span>
			</div>
		</aside>
	);
};

// ============ A single chart card on the canvas ============
const ChartCard = ({ chart, rows, typeOf, labelOf, t, selected, onSelect, onRemove, onDuplicate, height = CARD_CHART_HEIGHT }) => {
	const chartData = useMemo(
		() => buildChartData(rows, chart.config, typeOf, t),
		[rows, chart.config, typeOf, t]
	);
	const { chartTitle } = computeTitles(chart.config, labelOf, t);
	const displayTitle = (chart.config.title && chart.config.title.trim()) || chartTitle;

	return (
		<Card
			onClick={() => onSelect(chart.id)}
			style={{
				borderRadius: 10,
				cursor: 'pointer',
				height: '100%',
				// keep a constant 1px border (no layout shift); show selection with a soft ring instead of a thick border
				border: selected ? `1px solid ${BRAND}` : '1px solid #eceef1',
				boxShadow: selected ? '0 0 0 3px rgba(20,27,203,0.12)' : '0 1px 2px rgba(0,0,0,0.03)',
				transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
			}}
			styles={{
				header: { minHeight: 44, padding: '0 18px', borderBottom: '1px solid #f4f4f5' },
				body: { padding: '18px 22px 22px' },
			}}
			title={
				<span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{displayTitle}
				</span>
			}
			extra={
				<span style={{ display: 'inline-flex', gap: 2 }} onClick={(e) => e.stopPropagation()}>
					<Tooltip title={t('Duplicate')}>
						<Button type="text" size="small" icon={<CopyOutlined />} onClick={() => onDuplicate(chart.id)} />
					</Tooltip>
					<Tooltip title={t('Delete')}>
						<Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove(chart.id)} />
					</Tooltip>
				</span>
			}
		>
			{renderChartElement(chart.config, chartData, { rowsCount: rows.length, typeOf, labelOf, t, height })}
</Card>
	);
};

// ============ Main component: canvas of chart cards + shared config panel ============
const GraphView = ({ data = [], columns = [], availableColumns = [], spaceId, viewId, userInfo }) => {
	const { t } = useTranslation();
	const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

	// ---- Field discovery: every column available in "Customize Columns" ----
	// `availableColumns` is the full column catalogue (same source the Customize Columns modal uses):
	// each item is { value/id, label, formType }. We fall back to the selected view `columns`
	// ({ dataIndex, title }) when the catalogue isn't provided. Row data carries a value for every
	// column key, so any column is chartable. Type is inferred from sampled values (+ formType hint).
	const fields = useMemo(() => {
		// normalize whichever source we have into { key, label, formType }
		const source = (Array.isArray(availableColumns) && availableColumns.length)
			? availableColumns.map((c) => ({
				key: c.value ?? c.id,
				label: (typeof c.label === 'string' && c.label.trim()) ? c.label.trim() : undefined,
				formType: c.formType,
			}))
			: (columns || []).map((c) => ({
				key: c.dataIndex,
				label: (typeof c.title === 'string' && c.title.trim()) ? c.title.trim() : undefined,
				formType: undefined,
			}));

		const orderedKeys = [];
		const seen = new Set();
		const metaByKey = {};
		source.forEach((c) => {
			if (!c.key || seen.has(c.key)) return;
			seen.add(c.key);
			orderedKeys.push(c.key);
			metaByKey[c.key] = c;
		});

		const all = [];
		const labelByKey = {};
		const typeByKey = {};
		orderedKeys.forEach((k) => {
			if (EXCLUDED_KEYS.has(k)) return;
			const meta = metaByKey[k];
			const hasLabel = !!meta.label;
			// a column with no readable label AND a GUID key is unlabelable — skip it
			if (!hasLabel && isGuidLike(k)) return;

			const samples = [];
			for (let i = 0; i < rows.length && samples.length < 50; i++) {
				const v = rows[i]?.[k];
				if (v !== null && v !== undefined && v !== '') samples.push(v);
			}

			const keyName = k.toLowerCase();
			const looksDateKey = /date|time|createdon|createdat|updatedon|updatedat|deadline|expiry/.test(keyName);
			const numCount = samples.filter((v) => toNumber(v) !== undefined).length;
			const dateCount = samples.filter((v) => typeof v !== 'number' && (
				(v !== null && typeof v === 'object' && safeMoment(v).isValid())
				|| moment(v, moment.ISO_8601, true).isValid()
				|| (typeof v === 'string' && !isGuidLike(v) && /^\d{4}-\d{2}-\d{2}/.test(v))
			)).length;

			let type = 'categorical';
			if (samples.length > 0 && dateCount >= samples.length * 0.6 && (looksDateKey || dateCount === samples.length)) {
				type = 'date';
			} else if (samples.length > 0 && numCount >= samples.length * 0.8) {
				type = 'numeric';
			} else if (samples.length === 0 && meta.formType) {
				// no data on this page — fall back to the field's declared form type
				const ft = String(meta.formType).toLowerCase();
				if (/date|time/.test(ft)) type = 'date';
				else if (/number|numeric|currency|amount|decimal|integer|percent|money/.test(ft)) type = 'numeric';
			}

			const label = meta.label || prettifyKey(k);
			labelByKey[k] = label;
			typeByKey[k] = type;
			all.push({ value: k, label });
		});

		const numeric = all.filter((f) => typeByKey[f.value] === 'numeric');
		return { all, numeric, labelByKey, typeByKey };
	}, [availableColumns, columns, rows]);

	// stable helpers so ChartCard memoization holds
	const typeOf = useCallback((key) => fields.typeByKey[key] || 'categorical', [fields]);
	const labelOf = useCallback(
		(key) => (key === COUNT_FIELD ? t('Count of Requests') : (fields.labelByKey[key] || prettifyKey(key))),
		[fields, t]
	);

	// ---- Multi-chart state (persisted per space/view) ----
	const [charts, setCharts] = useState(null); // null = not initialized yet
	const [selectedId, setSelectedId] = useState(null);
	const [configCollapsed, setConfigCollapsed] = useState(false);

	// ---- Save-as-dashboard state ----
	const [saveOpen, setSaveOpen] = useState(false);
	const [saveName, setSaveName] = useState('');
	const [saveShared, setSaveShared] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editingView, setEditingView] = useState(null); // saved view the modal will update (null = create new)

	// ---- Saved-dashboard (API) state ----
	const [savedViews, setSavedViews] = useState([]);
	const [loadingViews, setLoadingViews] = useState(false);
	const [currentView, setCurrentView] = useState(null); // { id, name, sharedWithUsers } of the loaded dashboard
	const storageKey = `${STORAGE_VERSION}::${spaceId || 'x'}::${viewId || 'x'}`;
	const loadedKeyRef = useRef(null);
	const autoLoadedKeyRef = useRef(null); // storage key whose default dashboard has already been auto-selected
	const userId = userInfo?.userId;

	// (re)load whenever the space/view key changes, or once fields become available for a fresh key
	useEffect(() => {
		const keyChanged = loadedKeyRef.current !== storageKey;

		if (!keyChanged && charts !== null) return;

		let loaded = null;
		try {
			const raw = localStorage.getItem(storageKey);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (parsed && Array.isArray(parsed.charts) && parsed.charts.length) loaded = parsed;
			}
		} catch { /* ignore corrupt storage */ }

		if (loaded) {
			loadedKeyRef.current = storageKey;
			setCharts(loaded.charts);
			setSelectedId(
				loaded.selectedId && loaded.charts.some((c) => c.id === loaded.selectedId)
					? loaded.selectedId
					: loaded.charts[0].id
			);
			return;
		}
		if (fields.all.length) {
			loadedKeyRef.current = storageKey;
			const c = { id: uid(), config: defaultConfig(fields) };
			setCharts([c]);
			setSelectedId(c.id);
			return;
		}
		// nothing persisted and fields not ready yet — wait
		if (keyChanged) {
			loadedKeyRef.current = null;
			setCharts(null);
			setSelectedId(null);
		}
	}, [storageKey, fields, charts, selectedId]);

	// persist on change (only once this key has been loaded, so we never clobber before load)
	useEffect(() => {
		if (charts === null || loadedKeyRef.current !== storageKey) return;
		try {
			localStorage.setItem(storageKey, JSON.stringify({ charts, selectedId }));
		} catch { /* storage full / unavailable — ignore */ }
	}, [charts, selectedId, storageKey]);

	const list = useMemo(() => charts || [], [charts]);
	const selectedChart = list.find((c) => c.id === selectedId) || null;

	const updateSelected = useCallback((patch) => {
		setCharts((cs) => (cs || []).map((c) => (c.id === selectedId ? { ...c, config: { ...c.config, ...patch } } : c)));
	}, [selectedId]);

	const addChart = useCallback(() => {
		const c = { id: uid(), config: defaultConfig(fields) };
		setCharts([...list, c]);
		setSelectedId(c.id);
	}, [list, fields]);

	const removeChart = useCallback((id) => {
		const next = list.filter((c) => c.id !== id);
		setCharts(next);
		if (selectedId === id) setSelectedId(next[0]?.id ?? null);
	}, [list, selectedId]);

	const duplicateChart = useCallback((id) => {
		const i = list.findIndex((c) => c.id === id);
		if (i < 0) return;
		const copy = { id: uid(), config: { ...list[i].config } };
		const next = [...list];
		next.splice(i + 1, 0, copy);
		setCharts(next);
		setSelectedId(copy.id);
	}, [list]);

	const handlePanelResizeComplete = useCallback(() => {
		// Ant Design Charts measures its container; notify it after the layout animation settles.
		window.dispatchEvent(new Event('resize'));
	}, []);

	// ---- Load the list of saved dashboards for this space ----
	const fetchSavedViews = useCallback(async () => {
		if (!spaceId) return [];
		setLoadingViews(true);
		try {
			const res = await getGraphViews(spaceId);
			const views = extractGraphViewList(res).map(normalizeGraphView).filter((v) => v.id);
			setSavedViews(views);
			return views;
		} catch {
			// non-fatal — the builder still works without saved dashboards
			return [];
		} finally {
			setLoadingViews(false);
		}
	}, [spaceId]);

	// ---- Put a saved dashboard onto the canvas and make it the current selection ----
	const applyDashboard = useCallback((view, { silent = false } = {}) => {
		if (!view) return false;
		const next = (view.charts || []).map((c) => ({ id: uid(), config: apiChartToConfig(c) }));
		if (!next.length) {
			if (!silent) message.info(t('This dashboard has no charts.'));
			return false;
		}
		// claim the storage key so the local-canvas restore effect doesn't overwrite what we just loaded
		loadedKeyRef.current = storageKey;
		setCharts(next);
		setSelectedId(next[0].id);
		setCurrentView({ id: view.id, name: view.name, sharedWithUsers: view.sharedWithUsers });
		if (!silent) message.success(t('Dashboard loaded'));
		return true;
	}, [storageKey, t]);

	// Fetch the space's dashboards and open the user's default one as the current selection
	useEffect(() => {
		let cancelled = false;
		setCurrentView(null);
		autoLoadedKeyRef.current = null;
		fetchSavedViews().then((views) => {
			if (cancelled || !userId || autoLoadedKeyRef.current === storageKey) return;
			const preferred = views.find((v) => (v.defaultViewers || []).includes(userId));
			if (!preferred) return;
			autoLoadedKeyRef.current = storageKey;
			applyDashboard(preferred, { silent: true });
		});
		return () => { cancelled = true; };
	}, [fetchSavedViews, applyDashboard, storageKey, userId]);

	// ---- Open a saved dashboard onto the canvas (uses the already-fetched list data) ----
	const loadDashboard = useCallback((id) => {
		if (!id) return;
		// an explicit pick wins over the pending auto-selection of the default dashboard
		autoLoadedKeyRef.current = storageKey;
		applyDashboard(savedViews.find((v) => v.id === id));
	}, [savedViews, applyDashboard, storageKey]);

	// ---- Delete a saved dashboard ----
	const handleDeleteDashboard = useCallback(async (id) => {
		if (!id) return;
		try {
			await deleteGraphView(id);
			message.success(t('Dashboard deleted'));
			if (currentView?.id === id) setCurrentView(null);
			fetchSavedViews();
		} catch (err) {
			message.error(err?.response?.data?.message || t('Failed to delete dashboard.'));
		}
	}, [currentView, fetchSavedViews, t]);

	// ---- Make a dashboard the user's default view (moves default from the current one) ----
	const handleSetDefault = useCallback(async (view) => {
		const userId = userInfo?.userId;
		if (!userId || !view?.id) return;
		const isDefault = (view.defaultViewers || []).includes(userId);
		if (isDefault) return; // already the default — nothing to do
		const current = savedViews.find((v) => (v.defaultViewers || []).includes(userId));
		try {
			await setGraphViewDefault({
				userId,
				sourceGraphId: current?.id || '',
				destinationGraphId: view.id,
			});
			message.success(t('Set as default view'));
			fetchSavedViews();
		} catch (err) {
			message.error(err?.response?.data?.message || t('Failed to update default view.'));
		}
	}, [savedViews, userInfo, fetchSavedViews, t]);

	// Save current canvas as a brand-new dashboard
	const openSaveNew = useCallback(() => {
		setEditingView(null);
		setSaveName('');
		setSaveShared(false);
		setSaveOpen(true);
	}, []);

	// Open an existing dashboard for editing (loads it onto the canvas first)
	const openEditDashboard = useCallback((view) => {
		loadDashboard(view.id);
		setEditingView(view);
		setSaveName(view.name || '');
		setSaveShared(view.sharedWithUsers || false);
		setSaveOpen(true);
	}, [loadDashboard]);

	const handleSaveDashboard = useCallback(async () => {
		const name = saveName.trim();
		if (!name) { message.warning(t('Please enter a dashboard name')); return; }
		if (!spaceId) { message.error(t('Missing space — cannot save dashboard')); return; }
		if (!list.length) { message.warning(t('Add at least one chart before saving')); return; }

		const isUpdate = !!editingView?.id;
		const userId = userInfo?.userId;
		const payload = {
			name,
			spaceId,
			sharedWithUsers: saveShared,
			charts: list.map((chart) => chartToApiPayload(chart, labelOf, t)),
			...(isUpdate ? { graphViewId: editingView.id } : { defaultViewers: [userId] }),
		};

		setSaving(true);
		try {
			const res = await saveGraphView(payload);
			const saved = normalizeGraphView(res?.result ?? res?.data ?? res);
			setCurrentView({
				id: saved.id || (isUpdate ? editingView.id : undefined),
				name,
				sharedWithUsers: saveShared,
			});
			message.success(isUpdate ? t('Dashboard updated successfully') : t('Dashboard saved successfully'));
			setSaveOpen(false);
			fetchSavedViews();
		} catch (err) {
			const status = err?.response?.status;
			if (status === 409) message.error(t('A dashboard with this name already exists. Please choose a different name.'));
			else message.error(err?.response?.data?.message || t('Failed to save dashboard. Please try again.'));
		} finally {
			setSaving(false);
		}
	}, [saveName, saveShared, editingView, spaceId, list, labelOf, fetchSavedViews, userInfo, t]);

	return (
		<>
		<div
			style={{
				display: 'flex',
				gap: configCollapsed ? 12 : 20,
				alignItems: 'stretch',
				minHeight: 560,
				width: '100%',
				transition: 'gap 280ms cubic-bezier(0.22, 1, 0.36, 1)',
			}}
		>
			<ConfigPanel
				config={selectedChart?.config || null}
				fields={fields}
				typeOf={typeOf}
				onChange={updateSelected}
				t={t}
				collapsed={configCollapsed}
				onToggleCollapse={() => setConfigCollapsed((v) => !v)}
				onResizeComplete={handlePanelResizeComplete}
			/>

			<div style={{ flex: 1, minWidth: 0 }}>
				{/* Canvas toolbar */}
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
					<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
						<Button type="primary" icon={<PlusOutlined />} onClick={addChart} style={{ background: BRAND, borderColor: BRAND }}>
							{t('New Chart')}
						</Button>
						<Select
							className="ff-gv-dashboard-select"
							style={{ minWidth: 210 }}
							placeholder={t('Select dashboard')}
							value={currentView?.id}
							loading={loadingViews}
							onChange={loadDashboard}
							notFoundContent={loadingViews ? t('Loading…') : t('No saved dashboards')}
							suffixIcon={<FolderOpenOutlined />}
							options={savedViews.map((v) => ({ value: v.id, label: v.name, view: v }))}
							optionRender={(option) => {
								const v = option.data.view;
								const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
								const isDefault = !!userInfo?.userId && (v.defaultViewers || []).includes(userInfo.userId);
								return (
									<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
										<span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
										<span style={{ display: 'inline-flex', gap: 2, flexShrink: 0 }} onMouseDown={stop}>
											<Tooltip title={isDefault ? t('Default view') : t('Set as default view')}>
												<Button
													type="text"
													size="small"
													icon={isDefault ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
													onClick={(e) => { stop(e); if (!isDefault) handleSetDefault(v); }}
												/>
											</Tooltip>
											<Tooltip title={t('Edit')}>
												<Button type="text" size="small" icon={<EditOutlined />} onClick={(e) => { stop(e); openEditDashboard(v); }} />
											</Tooltip>
											<Popconfirm
												title={t('Delete this dashboard?')}
												description={t('This cannot be undone.')}
												okText={t('Delete')}
												okButtonProps={{ danger: true }}
												cancelText={t('Cancel')}
												onConfirm={() => handleDeleteDashboard(v.id)}
											>
												<Tooltip title={t('Delete')}>
													<Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={stop} />
												</Tooltip>
											</Popconfirm>
										</span>
									</div>
								);
							}}
							popupRender={(menu) => (
								<>
									{menu}
									<Divider style={{ margin: '4px 0' }} />
									<Button
										type="text"
										icon={<PlusOutlined />}
										disabled={!list.length}
										onMouseDown={(e) => e.preventDefault()}
										onClick={openSaveNew}
										style={{ width: '100%', textAlign: 'left', color: BRAND, fontWeight: 500 }}
									>
										{t('Save View as New')}
									</Button>
								</>
							)}
						/>
					</span>
					<span style={{ fontSize: 12, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
						<DatabaseOutlined style={{ fontSize: 11 }} />
						{list.length} {list.length === 1 ? t('chart') : t('charts')} · {rows.length} {t('records')}
					</span>
				</div>

				{list.length === 0 ? (
					<Card style={{ borderRadius: 10, border: '1px dashed #d9d9d9' }} styles={{ body: { padding: '56px 0' } }}>
						<Empty description={t('No charts yet')}>
							<Button type="primary" icon={<PlusOutlined />} onClick={addChart} style={{ background: BRAND, borderColor: BRAND }}>
								{t('New Chart')}
							</Button>
						</Empty>
					</Card>
				) : (
					<Row gutter={[16, 16]}>
						{list.map((chart) => {
							const single = list.length === 1;
							return (
								<Col xs={24} xl={single ? 24 : 12} key={chart.id}>
									<ChartCard
										chart={chart}
										rows={rows}
										typeOf={typeOf}
										labelOf={labelOf}
										t={t}
										height={single ? 440 : CARD_CHART_HEIGHT}
										selected={chart.id === selectedId}
										onSelect={setSelectedId}
										onRemove={removeChart}
										onDuplicate={duplicateChart}
									/>
								</Col>
							);
						})}
					</Row>
				)}
			</div>
		</div>

		<Modal
			rootClassName="ff-graphview-modal"
			title={editingView?.id ? t('Update Dashboard') : t('Save Dashboard')}
			open={saveOpen}
			onCancel={() => setSaveOpen(false)}
			onOk={handleSaveDashboard}
			confirmLoading={saving}
			okText={editingView?.id ? t('Update') : t('Save')}
			okButtonProps={{ style: { background: BRAND, borderColor: BRAND } }}
			destroyOnHidden
		>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 4 }}>
				<Field label={t('Dashboard Name')}>
					<Input
						autoFocus
						placeholder={t('e.g. Requests Overview')}
						value={saveName}
						onChange={(e) => setSaveName(e.target.value)}
						onPressEnter={handleSaveDashboard}
						maxLength={120}
					/>
				</Field>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<span style={{ fontSize: 13, color: '#374151' }}>{t('Share with other users in this space')}</span>
					<Switch checked={saveShared} onChange={setSaveShared} />
				</div>
				<div style={{ fontSize: 12, color: '#9ca3af' }}>
					{list.length} {list.length === 1 ? t('chart') : t('charts')} {t('will be saved to this dashboard.')}
				</div>
			</div>
		</Modal>
		</>
	);
};

export default GraphView;
